import { useEffect } from "react";
import { useGetCreditCardProductsQuery } from "@lending/hooks/queries/CreditCardProduct/useGetCreditCardProductsQuery";
import { useFlowType } from "@lending/hooks/useFlowType";
import { isInProgressApplicationState } from "@lending/utils/applicationState";
import {
  getSelectedCreditCardProduct,
  useApplicationNavigationContext,
} from "./applicationNavigationContext";
import { ResolvedApplicationLayout } from "./ResolvedApplicationLayout";
import { getCreatingResumeStepId } from "./resumeRouteResolver";
import { useResolvedApplicationRoute } from "./useResolvedApplicationRoute";
import { useStore } from "../../contexts/StoreContext";
import type { LendingApplication } from "./applicationNavigationContext";
import type { FlowType, ProductType } from "@lending/navigation/types";
import type { MembershipShareAccessResponse, StateEnum } from "@lending/types";

interface RouteResolutionApplication {
  state: StateEnum;
  account_application_uuid?: string | null;
}

interface QueryResult<TApplication> {
  data?: TApplication;
  isLoading: boolean;
  isError: boolean;
}

interface ApplicationLayoutProps<TApplication extends RouteResolutionApplication> {
  fallbackPath: string;
  productType: ProductType;
  useApplicationQuery: () => QueryResult<TApplication>;
  getRoutePolicy: (
    application: TApplication,
    currentStepId: string | null,
    membershipShareAccess: MembershipShareAccessResponse | undefined,
    flowType?: FlowType
  ) => {
    canonicalStepId: string | null;
  };
}

interface InProgressApplicationLayoutProps {
  fallbackPath: string;
  productType: ProductType;
  isLoading: boolean;
  isError: boolean;
  application: LendingApplication;
  applicationState?: StateEnum;
  accountApplicationUuid?: string | null;
  shouldAlwaysEnforceRoute: boolean;
  flowType: FlowType;
  canonicalStepId: string | null;
  hasMultipleCreditCardProducts?: boolean;
}

function InProgressApplicationLayout({
  fallbackPath,
  productType,
  isLoading,
  isError,
  application,
  applicationState,
  accountApplicationUuid,
  shouldAlwaysEnforceRoute,
  flowType,
  canonicalStepId: policyCanonicalStepId,
  hasMultipleCreditCardProducts,
}: InProgressApplicationLayoutProps) {
  const navigationContext = useApplicationNavigationContext(
    application,
    productType,
    undefined,
    hasMultipleCreditCardProducts
  );
  const canonicalStepId =
    application.state === "creating"
      ? getCreatingResumeStepId(application, productType, flowType, navigationContext)
      : policyCanonicalStepId;
  const { updateData } = useStore();
  const {
    skippedPlaidPrefill,
    hasCoApplicant,
    hasSeparateCoApplicantAddress,
    incomeType,
    coApplicantIncomeType,
    hasSelectedCreditCardProduct,
  } = navigationContext;
  const selectedProduct = getSelectedCreditCardProduct(application, productType);
  useEffect(() => {
    updateData({
      skippedPlaidPrefill,
      hasCoApplicant,
      hasSeparateCoApplicantAddress,
      incomeType,
      coApplicantIncomeType,
      hasSelectedCreditCardProduct,
      selectedProduct,
    });
  }, [
    coApplicantIncomeType,
    hasCoApplicant,
    hasSelectedCreditCardProduct,
    hasSeparateCoApplicantAddress,
    incomeType,
    selectedProduct,
    skippedPlaidPrefill,
    updateData,
  ]);

  return (
    <ResolvedApplicationLayout
      fallbackPath={fallbackPath}
      productType={productType}
      isLoading={isLoading}
      isError={isError}
      canonicalStepId={canonicalStepId}
      applicationState={applicationState}
      navigationContext={navigationContext}
      accountApplicationUuid={accountApplicationUuid}
      shouldAlwaysEnforceRoute={shouldAlwaysEnforceRoute}
    />
  );
}

function InProgressCreditCardApplicationLayout(
  props: Omit<InProgressApplicationLayoutProps, "hasMultipleCreditCardProducts">
) {
  const { hasMultipleProducts } = useGetCreditCardProductsQuery();

  return (
    <InProgressApplicationLayout {...props} hasMultipleCreditCardProducts={hasMultipleProducts} />
  );
}

function ApplicationLayout<TApplication extends RouteResolutionApplication>({
  fallbackPath,
  productType,
  useApplicationQuery,
  getRoutePolicy,
}: ApplicationLayoutProps<TApplication>) {
  const flowType = useFlowType();
  const {
    isLoading,
    isError,
    canonicalStepId,
    currentStepId,
    applicationState,
    application,
    accountApplicationUuid,
    shouldAlwaysEnforceRoute,
  } = useResolvedApplicationRoute({
    productType,
    useApplicationQuery,
    getCanonicalRoutePolicy: getRoutePolicy,
  });
  const resumeApplication = application as LendingApplication | undefined;

  if (
    resumeApplication &&
    isInProgressApplicationState(resumeApplication.state) &&
    currentStepId === null
  ) {
    const inProgressProps = {
      fallbackPath,
      productType,
      isLoading,
      isError,
      application: resumeApplication,
      applicationState,
      accountApplicationUuid,
      shouldAlwaysEnforceRoute,
      flowType,
      canonicalStepId,
    };

    if (productType === "credit-card") {
      return <InProgressCreditCardApplicationLayout {...inProgressProps} />;
    }

    return <InProgressApplicationLayout {...inProgressProps} />;
  }

  return (
    <ResolvedApplicationLayout
      fallbackPath={fallbackPath}
      productType={productType}
      isLoading={isLoading}
      isError={isError}
      canonicalStepId={canonicalStepId}
      applicationState={applicationState}
      accountApplicationUuid={accountApplicationUuid}
      shouldAlwaysEnforceRoute={shouldAlwaysEnforceRoute}
    />
  );
}

export { ApplicationLayout };
